import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import LeadHeader from "@/components/leads/LeadHeader";
import LeadOverview, { EDITABLE_FIELDS } from "@/components/leads/LeadOverview";
import LeadCaseDetails from "@/components/leads/LeadCaseDetails";
import LeadTimeline from "@/components/leads/LeadTimeline";
import LeadRevisions from "@/components/leads/LeadRevisions";
import LeadDelivery from "@/components/leads/LeadDelivery";
import LeadPayloads from "@/components/leads/LeadPayloads";
import { LeadRouteSummary } from "@/components/leads/LeadRouteSummary";

const TABS = ["overview", "timeline", "revisions", "delivery", "payload"];

/**
 * `LeadDetail` is a controller that fetches everything for a single lead and
 * hands each tab off to a focused presenter under `components/leads/`.
 */
export default function LeadDetail() {
  const { id } = useParams();
  const { user, hasPerm } = useAuth();
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [revisions, setRevisions] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  // The tab lives in the URL so links like ?tab=delivery always open it.
  const tab = TABS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "overview";
  const setTab = (k) => setSearchParams((p) => { const n = new URLSearchParams(p); n.set("tab", k); return n; }, { replace: true });
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({});
  const [reason, setReason] = useState("");
  const [share, setShare] = useState({ client: false, affiliate: false });
  const [verifying, setVerifying] = useState(false);

  const load = useCallback(async () => {
    const [d, t, r, a] = await Promise.all([
      api.get(`/leads/${id}`),
      api.get(`/leads/${id}/timeline`),
      api.get(`/leads/${id}/revisions`).catch(() => ({ data: { revisions: [] } })),
      api.get(`/delivery/attempts/${id}`).catch(() => ({ data: { attempts: [] } })),
    ]);
    setData(d.data);
    setTimeline(t.data.events);
    setRevisions(r.data.revisions);
    setAttempts(a.data.attempts);
    setEdit(d.data.lead);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const startEdit = () => { setEditing(true); setEdit(data.lead); };
  const cancelEdit = () => { setEditing(false); setEdit(data.lead); };

  const submitEdit = async () => {
    const changes = {};
    const orig = data.lead;
    EDITABLE_FIELDS.forEach((k) => {
      if ((edit[k] || "") !== (orig[k] || "")) changes[k] = edit[k] || null;
    });
    if (Object.keys(changes).length === 0) {
      toast.info("No changes to save");
      setEditing(false);
      return;
    }
    try {
      await api.patch(`/leads/${id}`, {
        changes,
        reason: reason || null,
        share_with_client: share.client,
        share_with_affiliate: share.affiliate,
      });
      toast.success("Lead updated");
      setEditing(false);
      setReason("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const verifyTf = async () => {
    setVerifying(true);
    try {
      const { data: r } = await api.post(`/trustedform/verify/${id}`);
      toast.success(`TrustedForm: ${r.status} (via ${r.source})`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setVerifying(false);
    }
  };

  if (!data) return <div className="label-mono">Loading…</div>;

  const canEdit = hasPerm("edit_lead_information");
  const canReport = hasPerm("report_bad_leads");
  const canExport = hasPerm("export_leads");
  const isInternal = user?.org_type === "internal";
  const isClientUser = user?.org_type === "client";

  return (
    <div>
      <LeadHeader
        lead={data.lead}
        canEdit={canEdit}
        canReport={canReport}
        canExport={canExport}
        editing={editing}
        onStartEdit={startEdit}
        onChanged={load}
      />

      {isInternal && <LeadRouteSummary lead={data.lead} />}

      <div className="flex items-center border-b border-black">
        {TABS.map((k) => (
          <button
            key={k}
            data-testid={`tab-${k}`}
            onClick={() => setTab(k)}
            className={`px-4 py-2 text-[11px] uppercase tracking-widest border-r border-black ${
              tab === k ? "bg-black text-white" : "hover:bg-zinc-100"
            }`}
          >
            {k}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <>
          <LeadOverview
            data={data}
            editing={editing}
            edit={edit}
            setEdit={setEdit}
            reason={reason}
            setReason={setReason}
            share={share}
            setShare={setShare}
            onCancel={cancelEdit}
            onSave={submitEdit}
            onVerifyTf={verifyTf}
            verifying={verifying}
            isInternal={isInternal}
            isClientUser={isClientUser}
          />
          <LeadCaseDetails lead={data.lead} />
        </>
      )}
      {tab === "timeline" && <LeadTimeline events={timeline} />}
      {tab === "revisions" && <LeadRevisions revisions={revisions} />}
      {tab === "payload" && <LeadPayloads lead={data.lead} />}
      {tab === "delivery" && <LeadDelivery attempts={attempts} lead={data.lead} onChanged={load} />}
    </div>
  );
}
